/**
 * SNIPPET - PREDICTIVE SEARCH
 */

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          form: this.querySelector('form'),
          input: this.querySelector('input[type="search"]'),
          predictiveSearchResults: this.querySelector('#predictive-search')
        };
    
        this.setupEventListeners();
      }
    
      // Creates the event listeners
      setupEventListeners() {
        this.elements.input.addEventListener('input', debounce((event) => {
          this.onChange(event);
        }, 300));
    
        this.elements.form.addEventListener('submit', this.onFormSubmit.bind(this));
        this.elements.input.addEventListener('focus', this.onFocus.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.addEventListener('keyup', this.onKeyup.bind(this));
      }
    
      // When the search term is changed
      onChange() {
        const searchTerm = this.getQuery();
    
        if (!searchTerm.length) {
          this.close();
          return;
        }
    
        this.getSearchResults(searchTerm);
      }

      // When the form is submitted
      onFormSubmit(event) {
        if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
          event.preventDefault();
          this.selectOption();
        }
      }
    
      // When the search input is focused
      onFocus() {
        const searchTerm = this.getQuery();
    
        if (!searchTerm.length) return;
    
        this.open();
      }
    
      // When the search input is focused out
      onFocusOut() {
        setTimeout(() => {
          if (!this.contains(document.activeElement)) this.close();
        })
      }
    
      // When a key is pressed
      onKeydown(event) {
        // Prevent the cursor from moving in the input when using the up and down arrow keys
        if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
          event.preventDefault();
        }
      }
    
      // When a key is released
      onKeyup(event) {
        if (!this.getQuery().length) this.close();
        event.preventDefault();
    
        switch (event.code) {
          case 'ArrowUp':
            this.switchOption('up')
            break;
          case 'ArrowDown':
            this.switchOption('down');
            break;
          case 'Enter':
            this.selectOption();
            break;
        }
      }
    
      // Gets the search terms
      getQuery() {
        return this.elements.input.value.trim();
      }
    
      // Gets the search results
      getSearchResults(searchTerm) {
        this.setLoadingState();
        
        fetch(`${window.theme.localeRootUrl}/search/suggest?q=${searchTerm}&section_id=predictive-search`)
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response.status);
              
              this.close();
              throw error;
            }
    
            return response.text();
          })
          .then((text) => {
            const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
            
            this.elements.predictiveSearchResults.innerHTML = resultsMarkup;
            this.elements.predictiveSearchResults.removeAttribute('loading');
            this.open();
          })
          .catch((error) => {
            this.close();
            throw error;
          });
      }
    
      // Displays the loading spinner
      setLoadingState() {
        this.elements.predictiveSearchResults.setAttribute('loading', true);
      }
    
      // Opens the searchs results
      open() {
        this.elements.predictiveSearchResults.setAttribute('open', true);
      }
    
      // Closes the searchs results
      close() {
        this.elements.predictiveSearchResults.removeAttribute('loading');
        this.elements.predictiveSearchResults.removeAttribute('open');
      }
    
      // Navigates from a search result to another
      switchOption(direction) {
        if (!this.elements.predictiveSearchResults.getAttribute('open')) return;
    
        const moveUp = direction === 'up';
        const selectedElement = this.querySelector('[aria-selected="true"]');
    
        // Filter out hidden elements (duplicated page and article resources) thanks
        // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
        const allVisibleElements = Array.from(
          this.querySelectorAll("li.predictive-search__list-item, button.predictive-search__item")
        ).filter((element) => element.offsetParent !== null);
        let activeElementIndex = 0;
    
        if (moveUp && !selectedElement) return;
    
        let selectedElementIndex = -1;
        let i = 0;
    
        while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
          if (allVisibleElements[i] === selectedElement) {
            selectedElementIndex = i;
          }
          i++;
        }
    
        if (!moveUp && selectedElement) {
          activeElementIndex =
            selectedElementIndex === allVisibleElements.length - 1
              ? 0
              : selectedElementIndex + 1;
        } else if (moveUp) {
          activeElementIndex =
            selectedElementIndex === 0
              ? allVisibleElements.length - 1
              : selectedElementIndex - 1;
        }
    
        if (activeElementIndex === selectedElementIndex) return;
    
        const activeElement = allVisibleElements[activeElementIndex];
    
        activeElement.setAttribute('aria-selected', true);
        if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    
        this.elements.input.setAttribute('aria-activedescendant', activeElement.id);
      }
    
      // Selects a search result
      selectOption() {
        const selectedOption = this.querySelector('li.predictive-search__list-item[aria-selected="true"] a, button.predictive-search__item[aria-selected="true"]');
    
        if (selectedOption) selectedOption.click();
      }
    }
  );
}